import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { shiftService } from '@/services/shiftService'
import type { ShiftListItem } from '@/api/types'

export const ShiftList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [shifts, setShifts] = useState<ShiftListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await shiftService.getAll(branchId, pageNumber, pageSize)
        setShifts(res.data ?? [])
        setTotalRecords(res.totalRecords ?? res.data?.length ?? 0)
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load shifts')
        setShifts([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize])

  const filteredShifts = useMemo(() => {
    if (!searchQuery.trim()) return shifts
    const q = searchQuery.toLowerCase()
    return shifts.filter(
      (s) =>
        (s.shiftDescription ?? s.description ?? '').toLowerCase().includes(q) ||
        (s.name ?? s.shiftType ?? '').toLowerCase().includes(q)
    )
  }, [shifts, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleDelete = async (row: ShiftListItem) => {
    const shiftId = row.id
    if (shiftId == null) return
    const name = row.name ?? row.shiftType ?? row.shiftDescription ?? row.description ?? 'this shift'
    const confirmed = await showDeleteConfirm(name, `Are you sure you want to delete "${name}"?`)
    if (!confirmed) return
    try {
      await shiftService.delete(shiftId)
      await showSuccess('Deleted', 'Shift deleted successfully.')
      if (branchId) {
        const res = await shiftService.getAll(branchId, pageNumber, pageSize)
        setShifts(res.data ?? [])
        setTotalRecords(res.totalRecords ?? res.data?.length ?? 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message || 'Failed to delete shift.')
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '--:--'
    if (timeStr.length <= 5 && timeStr.includes(':')) return timeStr
    try {
      const d = new Date(timeStr)
      return d.toTimeString().slice(0, 5)
    } catch {
      return timeStr
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Shift</h1>
            <p className="text-muted-foreground">Shift Detail</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <EmptyState title="Branch Not Selected" description="Please select a branch to view shifts." />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Shift</h1>
          <p className="text-muted-foreground">Shift Detail</p>
        </div>
        <Button onClick={() => navigate('/shifts/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Shift
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPageNumber(1) }}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="mt-4 text-muted-foreground">Loading shifts...</p>
            </div>
          ) : filteredShifts.length === 0 ? (
            <EmptyState
              title={searchQuery ? 'No shifts found' : 'No shifts yet'}
              description={searchQuery ? 'Try adjusting your search' : 'Add your first shift.'}
              action={!searchQuery ? { label: 'Add Shift', onClick: () => navigate('/shifts/new') } : undefined}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Shift Name
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Start Time
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        End Time
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Created Date
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[120px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredShifts.map((row) => (
                      <TableRow
                        key={row.id ?? row.description ?? row.shiftDescription}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {row.name ?? row.shiftType ?? row.shiftDescription ?? row.description ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatTime(row.shiftStartTime ?? row.startTime)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatTime(row.shiftEndTime ?? row.endTime)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(row.createdDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/shifts/${row.id}/edit`)}
                              title="Edit"
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row)}
                              title="Delete"
                              className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="border-t px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </Button>
                  <span className="text-sm">Page {pageNumber} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={pageNumber >= totalPages}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
